// shell/RightRail/RightRail.tsx — the shell-owned right column. Renders the page-published node.

import { useState } from 'react';
import type { ReactNode, Ref } from 'react';
import styled from 'styled-components';
import { useSession } from '@/hooks';
import { quietScrollbar } from '@/theme/scrollbar';
import { SIGNUP_CTA_BAR_HEIGHT } from '../SignupCtaBar';
import { Icon } from '@/icons';
import { RailToolbarContext } from './context';
import { RailHeader } from './RailHeader';
import { RailHeaderButton } from './RailHeader.styles';

const Aside = styled.aside<{ $guestBar: boolean; $collapsed: boolean }>`
  grid-column: 3;
  grid-row: 2;
  /* Fix-round item 14: same reservation as Rail.tsx and ShellLayout's Center:
     the guest SignupCtaBar is position:fixed/full-width and otherwise hides
     the last ~64px of whatever panel is scrolled to the bottom here. */
  width: 340px;
  height: 100%;
  box-sizing: border-box;
  padding: 0 0 ${({ $guestBar }) => ($guestBar ? 16 + SIGNUP_CTA_BAR_HEIGHT : 16)}px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transform: ${({ $collapsed }) => ($collapsed ? 'translateX(16px)' : 'none')};
  transition:
    opacity 180ms ease,
    transform 260ms cubic-bezier(0.22, 0.8, 0.25, 1);
  ${quietScrollbar}
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Contents = styled.div`
  padding: 16px 14px 0;
`;

export function RightRail({
  children,
  collapsed = false,
  hasToggle = false,
  onCollapse,
  collapseRef,
}: {
  children: ReactNode;
  collapsed?: boolean;
  hasToggle?: boolean;
  onCollapse?: () => void;
  collapseRef?: Ref<HTMLButtonElement>;
}) {
  const { isAuthenticated } = useSession();
  const [toolbar, setToolbar] = useState<HTMLSpanElement | null>(null);
  return (
    <Aside
      id="right-sidebar"
      aria-label="Secondary content"
      data-testid="right-rail"
      inert={collapsed}
      aria-hidden={collapsed || undefined}
      $guestBar={!isAuthenticated}
      $collapsed={collapsed}
    >
      {hasToggle && (
        <RailHeader>
          <span ref={setToolbar} />
          <RailHeaderButton
            ref={collapseRef}
            type="button"
            aria-label="Collapse right sidebar"
            title="Collapse right sidebar"
            aria-expanded={!collapsed}
            aria-controls="right-sidebar"
            onClick={onCollapse}
          >
            <Icon name="panel-right" size={18} />
          </RailHeaderButton>
        </RailHeader>
      )}
      <RailToolbarContext.Provider value={toolbar}>
        <Contents>{children}</Contents>
      </RailToolbarContext.Provider>
    </Aside>
  );
}
